import {
  SAFETY_CASES,
  SAFETY_COMPOSITE_EVENT,
  SAFETY_EVIDENCE_METRICS,
  SAFETY_FEATURES,
  SAFETY_INNOVATION_PRODUCT,
  SAFETY_MONITORING_DASHBOARD,
  SAFETY_MONITORING_DASHBOARD_ALT,
  SAFETY_MONITORING_FEATURES,
  SAFETY_SOUND_CLUSTERS,
  type SafetyCasePhoto,
} from '@/data/shared/safetyCopy';

import type { LanguageType } from '@/types/language';

type Translate = (key: string) => string;

/**
 * ### useSafetyFeatures
 *
 * Resolves the three lead capabilities into the page language; the icon is
 * passed through as authored.
 *
 * @param t Translator of the `common` namespace
 * @returns The features in published order
 */
export function useSafetyFeatures(t: Translate) {
  return SAFETY_FEATURES.map((feature) => ({
    id: feature.id,
    icon: feature.icon,
    title: t(feature.title),
    description: t(feature.description),
  }));
}

/**
 * ### useSafetyEvidenceMetrics
 *
 * Resolves the three credentials into the page language. A value that
 * carries words (`3건`) is translated too; `metricValueTranslations.test.ts`
 * holds every translation to the same digits as the authored value, so no
 * language can drift to a different number.
 *
 * @param t Translator of the `common` namespace
 * @returns The credentials in published order
 */
export function useSafetyEvidenceMetrics(t: Translate) {
  return SAFETY_EVIDENCE_METRICS.map((metric) => ({
    value: t(metric.value),
    label: t(metric.label),
    scope: t(metric.scope),
  }));
}

/**
 * ### useSafetyCompositeEvent
 *
 * Resolves the three explanatory points under the composite-event pop-up.
 *
 * @param t Translator of the `common` namespace
 * @returns The points in published order
 */
export function useSafetyCompositeEvent(t: Translate) {
  return {
    points: SAFETY_COMPOSITE_EVENT.points.map((point) => ({
      title: t(point.title),
      description: t(point.description),
    })),
  };
}

/**
 * ### useSafetySoundClusters
 *
 * Resolves the situation groups offered in the page language and every
 * sound inside them; a group limited to other languages is left out.
 *
 * @param t Translator of the `common` namespace
 * @param lang Page language
 * @returns The clusters in published order
 */
export function useSafetySoundClusters(t: Translate, lang: LanguageType) {
  return SAFETY_SOUND_CLUSTERS.filter(
    (cluster) => !cluster.languages || cluster.languages.includes(lang),
  ).map((cluster) => ({
    id: cluster.id,
    title: t(cluster.title),
    description: t(cluster.description),
    sounds: cluster.sounds.map((sound) => t(sound)),
  }));
}

/**
 * ### useSafetyMonitoringDashboard
 *
 * Resolves the dashboard capture for the page language with its
 * alternative text.
 *
 * @param t Translator of the `common` namespace
 * @param lang Page language
 * @returns The capture and its description
 */
export function useSafetyMonitoringDashboard(t: Translate, lang: LanguageType) {
  return {
    image: SAFETY_MONITORING_DASHBOARD[lang],
    alt: t(SAFETY_MONITORING_DASHBOARD_ALT),
  };
}

/**
 * ### useSafetyMonitoringFeatures
 *
 * Resolves the six monitoring capabilities into the page language.
 *
 * @param t Translator of the `common` namespace
 * @returns The capabilities in published order
 */
export function useSafetyMonitoringFeatures(t: Translate) {
  return SAFETY_MONITORING_FEATURES.map((feature) => ({
    title: t(feature.title),
    description: t(feature.description),
  }));
}

/**
 * ### SafetyCasePhotoView
 *
 * One installation photo with its caption resolved to the page language.
 */
export type SafetyCasePhotoView = Omit<SafetyCasePhoto, 'caption'> & {
  caption: string;
};

/**
 * ### useSafetyCases
 *
 * Resolves the three deployments; logos and photos pass through untranslated.
 *
 * @param t Translator of the `common` namespace
 * @returns The cases in published order
 */
export function useSafetyCases(t: Translate) {
  return SAFETY_CASES.map((item) => ({
    id: item.id,
    customer: t(item.customer),
    logo: item.logo,
    title: t(item.title),
    points: item.points.map((point) => t(point)),
    photos: item.photos.map((photo): SafetyCasePhotoView => ({
      src: photo.src,
      width: photo.width,
      height: photo.height,
      caption: t(photo.caption),
    })),
  }));
}

/**
 * ### useSafetyInnovationProduct
 *
 * Resolves the innovative-product statement. The pilot count and its date
 * travel inside one string, so translating never splits them.
 *
 * @param t Translator of the `common` namespace
 * @returns The statement, link label, and destination
 */
export function useSafetyInnovationProduct(t: Translate) {
  return {
    eyebrow: t(SAFETY_INNOVATION_PRODUCT.eyebrow),
    statement: t(SAFETY_INNOVATION_PRODUCT.statement),
    mark: SAFETY_INNOVATION_PRODUCT.mark,
    markAlt: t(SAFETY_INNOVATION_PRODUCT.markAlt),
    institutionsLabel: t(SAFETY_INNOVATION_PRODUCT.institutionsLabel),
    institutions: SAFETY_INNOVATION_PRODUCT.institutions.map((name) => t(name)),
    linkLabel: t(SAFETY_INNOVATION_PRODUCT.linkLabel),
    href: SAFETY_INNOVATION_PRODUCT.href,
  };
}
